import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { contentAPI, settingsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Headphones, Play, RotateCcw, Trophy, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ComprehensionOrale() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [series, setSeries] = useState([]);
  const [userProgress, setUserProgress] = useState({});
  const [accessLimit, setAccessLimit] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load all data based on user's center
        let packs, siteSettings;
        
        let seriesPromise = contentAPI.getSeries('comprehension_orale');

        if (user?.center_id) {
          [packs, siteSettings] = await Promise.all([
            settingsAPI.getCenterPacks(user.center_id),
            settingsAPI.getCenterAccessConfig(user.center_id)
          ]);
        } else {
          [packs, siteSettings] = await Promise.all([
            settingsAPI.getPacks(),
            settingsAPI.getSiteSettings()
          ]);
        }

        const loadedSeries = await seriesPromise;
        setSeries(Array.isArray(loadedSeries) ? loadedSeries : []);
        
        if (user?.role === 'admin' || user?.role === 'super_admin') {
          setAccessLimit(9999);
        } else {
          const freeBlocs = siteSettings?.free_blocs?.comprehension_orale || 1;
          if (user?.type_pack && user?.fin_abonnement) {
            const isSubscriptionActive = new Date(user.fin_abonnement) > new Date();
            if (isSubscriptionActive) {
              const userPack = packs?.find(p => p.id === user.type_pack || p.name === user.type_pack);
              if (userPack?.blocs_limits?.comprehension_orale) {
                const packLimit = userPack.blocs_limits.comprehension_orale;
                setAccessLimit(packLimit === -1 ? 9999 : packLimit);
              } else {
                setAccessLimit(freeBlocs);
              }
            } else {
              setAccessLimit(freeBlocs);
            }
          } else {
            setAccessLimit(freeBlocs);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Erreur de chargement');
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  const isSerieLocked = (index) => {
    return index >= accessLimit;
  };

  const getSerieProgress = (serieId) => {
    return userProgress[serieId] || null;
  };

  const handleStartExercise = (serieId, index) => {
    if (isSerieLocked(index)) {
      toast.info('🔒 Contenu Premium ! Découvrez nos forfaits pour débloquer toutes les séries.', {
        duration: 4000,
        action: {
          label: 'Voir les forfaits',
          onClick: () => navigate('/tarification')
        }
      });
      navigate('/tarification');
      return;
    }
    navigate(`/modules/comprehension-orale/exercise?serie=${serieId}`);
  };

  // Show series with at least 1 question
  const availableSeries = series.filter(s => s.questions?.length > 0);

  return (
    <Layout>
      <div className="space-y-8 animate-in">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center p-4 bg-green-500/10 rounded-2xl mb-4">
            <Headphones className="h-12 w-12 text-green-500" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Compréhension Orale</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Entraînez votre écoute et compréhension audio. Chaque série peut contenir jusqu'à 39 questions.
          </p>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 className="h-12 w-12 text-green-500 animate-spin mb-4" />
            <p className="text-muted-foreground">Chargement des exercices...</p>
          </div>
        ) : availableSeries.length === 0 ? (
          <div className="bg-card p-12 rounded-xl border text-center max-w-2xl mx-auto">
            <Headphones className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-semibold mb-2">Aucun exercice disponible</h3>
            <p className="text-muted-foreground">
              L'administrateur n'a pas encore créé de séries d'exercices pour ce module.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {availableSeries.map((serie, index) => {
              const progress = getSerieProgress(serie.id);
              const isNew = !progress;
              const bestScore = progress?.bestScore || 0;
              const attempts = progress?.attempts || 0;
              const totalQuestions = serie.questions?.length || 0;
              const maxPoints = serie.questions?.reduce((sum, q) => sum + (q.points || 0), 0) || 0;
              const scorePercent = maxPoints > 0 ? Math.round((bestScore / maxPoints) * 100) : 0;
              const isComplete = totalQuestions === 39;
              const locked = isSerieLocked(index);
              
              return (
                <div
                  key={serie.id}
                  data-testid={`bloc-${serie.number}`}
                  onClick={() => locked && navigate('/tarification')}
                  className={`bg-card rounded-2xl border-2 transition-all overflow-hidden group relative ${
                    locked 
                      ? 'border-gray-300 dark:border-gray-700 opacity-75 cursor-pointer hover:border-orange-500/50' 
                      : 'border-border hover:border-green-500/50'
                  }`}
                >
                  {/* Lock Overlay */}
                  {locked && (
                    <div className="absolute inset-0 bg-gray-900/10 dark:bg-gray-900/30 z-10 flex items-center justify-center">
                      <div className="bg-white dark:bg-gray-800 rounded-full p-4 shadow-lg">
                        <Lock className="h-8 w-8 text-gray-500 dark:text-gray-400" />
                      </div>
                    </div>
                  )}

                  {/* Card Header with Badge */}
                  <div className="p-6 pb-4">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className={`text-2xl font-bold ${locked ? 'text-gray-500' : 'text-green-500'}`}>
                        {serie.name}
                      </h3>
                      {locked ? (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                          Premium
                        </span>
                      ) : isNew ? (
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          isComplete ? 'bg-secondary' : 'bg-yellow-500/10 text-yellow-600'
                        }`}>
                          {isComplete ? 'Nouveau' : `${totalQuestions}/39`}
                        </span>
                      ) : (
                        <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${
                          scorePercent === 100 ? 'bg-green-500' :
                          scorePercent >= 80 ? 'bg-blue-500' :
                          scorePercent >= 60 ? 'bg-yellow-500' :
                          'bg-orange-500'
                        }`}>
                          {scorePercent}%
                        </span>
                      )}
                    </div>
                    
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <p>{totalQuestions} question{totalQuestions > 1 ? 's' : ''} • {maxPoints} pts max</p>
                      {!isNew && !locked && (
                        <p>Tentatives: {attempts}</p>
                      )}
                    </div>
                  </div>

                  {/* Progress bar for completed */}
                  {!isNew && !locked && (
                    <div className="px-6 pb-4">
                      <div className="h-2 bg-secondary rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            scorePercent === 100 ? 'bg-green-500' :
                            scorePercent >= 80 ? 'bg-blue-500' :
                            scorePercent >= 60 ? 'bg-yellow-500' :
                            'bg-orange-500'
                          }`}
                          style={{ width: `${scorePercent}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Action Button */}
                  <div className="p-6 pt-2">
                    {locked ? (
                      <button
                        onClick={() => navigate('/tarification')}
                        data-testid={`unlock-bloc-${serie.number}`}
                        className="w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white hover:from-orange-600 hover:to-amber-600"
                      >
                        <Lock className="h-5 w-5" />
                        <span>Débloquer</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleStartExercise(serie.id, index)}
                        data-testid={`start-bloc-${serie.number}`}
                        className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 ${
                          isNew 
                            ? 'bg-green-500 text-white hover:bg-green-600' 
                            : 'bg-green-500/10 text-green-600 hover:bg-green-500 hover:text-white border-2 border-green-500/30'
                        }`}
                      >
                        {isNew ? (
                          <>
                            <Play className="h-5 w-5" />
                            <span>Commencer</span>
                          </>
                        ) : (
                          <>
                            <RotateCcw className="h-5 w-5" />
                            <span>Recommencer</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Stats summary if user has progress */}
        {Object.keys(userProgress).length > 0 && (
          <div className="bg-card p-6 rounded-xl border max-w-2xl mx-auto">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-green-500/10 rounded-xl">
                <Trophy className="h-8 w-8 text-green-500" />
              </div>
              <div>
                <p className="font-semibold">Progression globale</p>
                <p className="text-sm text-muted-foreground">
                  {Object.keys(userProgress).length} série(s) tentée(s)
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
